import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Stack,
  useMediaQuery,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useTheme } from "@mui/material/styles";
import { fetchArticleById } from "./ArticleService";
import { fetchPickArticleGroups } from "../../Services/FetchArticleGroups";
import { fetchPickUnit } from "../../Services/FetchUnitsList";
import { fetchVatRates } from "../../Services/ArticleService";

const ledgers = [
  { Ledgerid: 107, LedgerName: "107 Artikull Mall" },
  { Ledgerid: 702, LedgerName: "702 Artikull Sherbim" },
];

const saleTypeMap = { 1: "Pakicë", 2: "Shumicë" };

const articleProductionLabel = (val) => {
  if (val == null || val === 1) return "Artikull i Thjeshtë";
  if (val === 2) return "Gjysëm Produkt";
  return "Produkt i Prodhuar";
};

const currencyFmt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFmt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// ─── ViewField: a single read-only labeled field ──────────────────────────────
function ViewField({
  label,
  value,
  fullWidth = false,
  highlight = false,
  md,
  lg,
}) {
  return (
    <Grid item xs={12} md={fullWidth ? 12 : md ?? 6} lg={fullWidth ? 12 : lg ?? md ?? 6}>
      <Box
        sx={{
          p: 1.6,
          border: "1px solid",
          borderColor: highlight ? "#c3dfd5" : "#e5e7eb",
          borderRadius: 2.5,
          backgroundColor: highlight ? "#f0faf6" : "#ffffff",
          height: "100%",
          boxShadow: highlight
            ? "0 6px 18px rgba(40,73,69,0.08)"
            : "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#4e7b67",
            mb: 0.4,
            fontWeight: 700,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            fontSize: "0.68rem",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#1e3a35",
            fontWeight: highlight ? 800 : 600,
            fontSize: highlight ? "1rem" : "0.92rem",
            wordBreak: "break-word",
          }}
        >
          {value ?? "—"}
        </Typography>
      </Box>
    </Grid>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        backgroundColor: "rgba(255,255,255,0.75)",
        mb: 2,
        boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: "#284945",
          fontWeight: 800,
          mb: 1.25,
          letterSpacing: 0.2,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          "&::before": {
            content: '""',
            display: "inline-block",
            width: "3px",
            height: "0.9rem",
            background: "#4e7b67",
            borderRadius: "2px",
          },
        }}
      >
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </Box>
  );
}

function InfoBadge({ icon, label, tone = "neutral" }) {
  const tones = {
    neutral: {
      backgroundColor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.2)",
      color: "#fff",
    },
    success: {
      backgroundColor: "rgba(22,163,74,0.18)",
      border: "1px solid rgba(134,239,172,0.35)",
      color: "#eafff0",
    },
    muted: {
      backgroundColor: "rgba(148,163,184,0.18)",
      border: "1px solid rgba(226,232,240,0.25)",
      color: "#f8fafc",
    },
    info: {
      backgroundColor: "rgba(59,130,246,0.18)",
      border: "1px solid rgba(147,197,253,0.35)",
      color: "#eff6ff",
    },
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.15,
        py: 0.7,
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: 0.2,
        lineHeight: 1,
        ...tones[tone],
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", fontSize: "0.95rem" }}>
        {icon}
      </Box>
      {label}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ArticleViewModal({ isOpen, onClose, articleId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [article, setArticle] = useState(null);
  const [groups, setGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !articleId) return;

    const load = async () => {
      setLoading(true);
      try {
        const [data, fetchedGroups, fetchedUnits, fetchedVat] =
          await Promise.all([
            fetchArticleById(articleId),
            fetchPickArticleGroups(),
            fetchPickUnit(),
            fetchVatRates(),
          ]);
        setArticle(data);
        setGroups(fetchedGroups);
        setUnits(fetchedUnits);
        setVatRates(fetchedVat);
      } catch {
        // gabimi nuk ndikon UX — modalin e mbyll useri
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, articleId]);

  const handleClose = () => {
    onClose();
    setArticle(null);
  };

  const groupName =
    groups.find((g) => g.GroupID === article?.GroupID)?.GroupName ?? "—";
  const unitName =
    units.find((u) => u.UnitID === article?.UnitID)?.UnitName ?? "—";
  const ledgerName =
    ledgers.find((l) => l.Ledgerid === article?.LedgerID)?.LedgerName ?? "—";
  const vatName =
    vatRates.find((v) => v.VATID === article?.VAT)?.VATName ?? "—";

  const unitLookup = (id) =>
    units.find((u) => u.UnitID === id)?.UnitName ?? id ?? "—";

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          background:
            "radial-gradient(circle at top right, rgba(40,73,69,0.07) 0%, rgba(40,73,69,0) 40%), #f7faf9",
          minHeight: { xs: "100dvh", sm: "auto" },
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2 },
          background: "linear-gradient(135deg, #1e3a35 0%, #284945 100%)",
          color: "#fff",
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          {/* Avatar me iniciale nga barkodi */}
          <Box
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
              borderRadius: 2.5,
              backgroundColor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.82rem",
              fontWeight: 800,
              letterSpacing: 0.5,
              flexShrink: 0,
            }}
          >
            {article?.ArticleName?.charAt(0)?.toUpperCase() ?? "A"}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                lineHeight: 1.15,
                fontSize: { xs: "0.95rem", sm: "1rem" },
                pr: 1,
              }}
            >
              {loading
                ? "Duke ngarkuar..."
                : article?.ArticleName ?? "Detaje Artikulli"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.74, mt: 0.25, fontSize: "0.75rem" }}
            >
              Shikim i vetëm — pa mundësi ndryshimi
            </Typography>
          </Box>
        </Box>

        {!loading && article && (
          <Stack
            direction="row"
            useFlexGap
            flexWrap="wrap"
            sx={{
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              width: { xs: "100%", sm: "auto" },
              gap: 0.75,
            }}
          >
            <InfoBadge
              icon={<Inventory2OutlinedIcon sx={{ fontSize: "0.95rem" }} />}
              label={articleProductionLabel(article.ArticleProduction)}
            />
            <InfoBadge
              icon={
                article.IsActive ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: "0.95rem" }} />
                ) : (
                  <CancelOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                )
              }
              label={article.IsActive ? "Aktiv" : "Jo Aktiv"}
              tone={article.IsActive ? "success" : "muted"}
            />
            {article.TouchScreen && (
              <InfoBadge
                icon={<PointOfSaleOutlinedIcon sx={{ fontSize: "0.95rem" }} />}
                label="POS"
                tone="info"
              />
            )}
          </Stack>
        )}
      </Box>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 3 }, pt: { xs: 1.5, sm: 2.5 } }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 180,
              gap: 1.5,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#4e7b67" }} />
            <Typography variant="body2" sx={{ color: "#4e7b67", fontWeight: 500 }}>
              Duke ngarkuar të dhënat...
            </Typography>
          </Box>
        ) : article ? (
          <>
            {/* Informata Bazë */}
            <Section title="Informata Bazë">
              <ViewField
                label="Emri i Artikullit"
                value={article.ArticleName}
                fullWidth
              />
              <ViewField label="Barkodi" value={article.Barcode} md={4} lg={4} />
              <ViewField
                label="Lloji i Artikullit"
                value={articleProductionLabel(article.ArticleProduction)}
                md={4}
                lg={4}
              />
              <ViewField
                label="Statusi"
                value={article.IsActive ? "Aktiv" : "Jo Aktiv"}
                md={4}
                lg={4}
              />
              <ViewField
                label="Përshkrimi"
                value={article.Description || "—"}
                fullWidth
              />
            </Section>

            {/* Kategorizimi */}
            <Section title="Kategorizimi">
              <ViewField label="Grupi" value={groupName} md={6} lg={3} />
              <ViewField label="Njësia Bazë" value={unitName} md={6} lg={3} />
              <ViewField label="Lloji Kontabël" value={ledgerName} md={6} lg={3} />
              <ViewField label="Norma TVSH" value={vatName} md={6} lg={3} />
            </Section>

            {/* Çmimet */}
            <Section title="Çmimet">
              <ViewField
                label="Çmimi Blerjes (me TVSH)"
                value={`€ ${currencyFmt.format(article.PurchasePrice ?? 0)}`}
                highlight
                md={6}
                lg={6}
              />
              <ViewField
                label="Çmimi Shitjes (me TVSH)"
                value={`€ ${currencyFmt.format(article.SalesPrice ?? 0)}`}
                highlight
                md={6}
                lg={6}
              />
              <ViewField
                label="Diferenca"
                value={`€ ${currencyFmt.format(
                  (article.SalesPrice ?? 0) - (article.PurchasePrice ?? 0),
                )}`}
                md={6}
                lg={6}
              />
              <ViewField
                label="Marzha"
                value={`${percentFmt.format(
                  article.PurchasePrice > 0
                    ? ((((article.SalesPrice ?? 0) - (article.PurchasePrice ?? 0)) /
                        article.PurchasePrice) *
                        100)
                    : 0,
                )} %`}
                md={6}
                lg={6}
              />
            </Section>

            {/* Detajet (tabela) */}
            {article.ArticleOtherDetails?.length > 0 && (
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  backgroundColor: "rgba(255,255,255,0.75)",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#284945",
                    fontWeight: 800,
                    mb: 1.5,
                    letterSpacing: 0.2,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    "&::before": {
                      content: '""',
                      display: "inline-block",
                      width: "3px",
                      height: "0.9rem",
                      background: "#4e7b67",
                      borderRadius: "2px",
                    },
                  }}
                >
                  Detajet e Çmimeve
                </Typography>
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 2.5,
                      overflow: "hidden",
                      boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            background:
                              "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
                          }}
                        >
                          {[
                            "Barkodi",
                            "Lloji Shitjes",
                            "Njësia",
                            "Sasia Shumicës",
                            "Çmimi Shitjes",
                          ].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                color: "#64748b",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                borderBottom: "1px solid #e5e7eb",
                                py: 1.25,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {article.ArticleOtherDetails.map((d, i) => (
                          <TableRow
                            key={i}
                            sx={{
                              "&:hover": { backgroundColor: "#f0faf6" },
                              "&:last-child td": { borderBottom: 0 },
                            }}
                          >
                            <TableCell sx={{ fontSize: "0.8125rem" }}>
                              {d.NewBarcode || "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem" }}>
                              <Chip
                                label={
                                  saleTypeMap[d.TypeOfSales] ?? d.TypeOfSales
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    d.TypeOfSales === 2 ? "#dbeafe" : "#dcfce7",
                                  color:
                                    d.TypeOfSales === 2 ? "#1d4ed8" : "#15803d",
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem" }}>
                              {unitLookup(d.ArticleUnitID)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem" }}>
                              {d.ArticleUnitQty ?? "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                              € {currencyFmt.format(d.NewSalesPrice ?? 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  spacing={1.25}
                  sx={{ display: { xs: "flex", md: "none" } }}
                >
                  {article.ArticleOtherDetails.map((d, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        border: "1px solid #dce8e2",
                        background:
                          "linear-gradient(180deg, #ffffff 0%, #f8fbfa 100%)",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.25,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 800,
                            color: "#1e3a35",
                          }}
                        >
                          Linja #{i + 1}
                        </Typography>
                        <Chip
                          label={saleTypeMap[d.TypeOfSales] ?? d.TypeOfSales}
                          size="small"
                          sx={{
                            backgroundColor:
                              d.TypeOfSales === 2 ? "#dbeafe" : "#dcfce7",
                            color:
                              d.TypeOfSales === 2 ? "#1d4ed8" : "#15803d",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>

                      <Grid container spacing={1.1}>
                        <ViewField label="Barkodi" value={d.NewBarcode || "—"} md={6} lg={6} />
                        <ViewField
                          label="Njësia"
                          value={unitLookup(d.ArticleUnitID)}
                          md={6}
                          lg={6}
                        />
                        <ViewField
                          label="Sasia Shumicës"
                          value={d.ArticleUnitQty ?? "—"}
                          md={6}
                          lg={6}
                        />
                        <ViewField
                          label="Çmimi Shitjes"
                          value={`€ ${currencyFmt.format(d.NewSalesPrice ?? 0)}`}
                          md={6}
                          lg={6}
                          highlight
                        />
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: "#6b7280", p: 2 }}>
            Nuk u gjetën të dhëna.
          </Typography>
        )}
      </DialogContent>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 3.5 },
          py: { xs: 1.25, sm: 2 },
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <Button
          onClick={handleClose}
          variant="contained"
          disableElevation
          sx={{
            background: "linear-gradient(135deg, #1e3a35 0%, #284945 100%)",
            px: 3.5,
            fontWeight: 700,
            borderRadius: 2,
            fontSize: "0.875rem",
            "&:hover": {
              background: "linear-gradient(135deg, #162c29 0%, #1f3835 100%)",
            },
          }}
        >
          Mbyll
        </Button>
      </DialogActions>
    </Dialog>
  );
}
